import { Box, Divider, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { Image3D } from '~/domain/patient/image3d';
import {
    domainEventBus,
    RegistrationCreateRequestedEvent,
} from '~/domain/events';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useSecondaryImage from '~/hooks/image/useSecondaryImage';
import {
    useRegistrationState,
    useSelectRegistration,
} from '~/hooks/registration/useRegistrationStore';
import { environmentState } from '~/state/atoms/engineAtoms';

import StepHeader from '../StepHeader';
import ImageList from './ImageRegistrations/components/ImageList';
import PrimaryImageHeader from './ImageRegistrations/components/PrimaryImageHeader';
import RegistrationPanel from './ImageRegistrations/RegistrationPanel';

export default function RegistrationContent() {
    const { primaryImage, availableImages, handleChangePrimaryImage } =
        usePrimaryImage(true);

    if (!primaryImage) {
        return (
            <Box>
                <StepHeader titleId="workflow.imageRegistration" />
                <Box display="flex" px={1.5} pt={1}>
                    <Typography color="text.secondary" fontSize={12}>
                        Select primary image...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box height="100%" display="flex" flexDirection="column">
            <RegistrationContentInner
                primaryImage={primaryImage}
                availableImages={availableImages}
                onChangePrimaryImage={handleChangePrimaryImage}
            />
        </Box>
    );
}

interface RegistrationContentInnerProps {
    primaryImage: Image3D;
    availableImages: AvailableImage[];
    onChangePrimaryImage: (image: AvailableImage) => void;
}

function RegistrationContentInner({
    primaryImage,
    availableImages,
    onChangePrimaryImage,
}: RegistrationContentInnerProps) {
    const [selectedImageUid, setSelectedImageUid] = useState<
        string | undefined
    >();
    const [isDetailsPanelExpanded, setIsDetailsPanelExpanded] = useState(true);
    const [createRegImage, setCreateRegImage] = useState<
        AvailableImage | undefined
    >();
    const intl = useIntl();

    const registrationState = useRegistrationState();
    const selectRegistration = useSelectRegistration();
    const { selectedSecondaryImage, handleChangeSecondaryImage } =
        useSecondaryImage();
    const environment = useRecoilValue(environmentState);

    // When entering the registration tab, clear any blend that uses
    // an inverted registration (secondary→primary direction). Inverted
    // registrations work for blending (the pipeline inverts the matrix)
    // but must not be active/editable in the registration tab.
    useEffect(() => {
        const activeRegistration = environment?.registration;
        if (
            activeRegistration &&
            selectedSecondaryImage &&
            activeRegistration.sourceFrameOfReferenceUid !==
                primaryImage.frameOfReferenceUid
        ) {
            handleChangeSecondaryImage(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Sync from registration store when a registration is active
        if (registrationState.selectedSopInstanceUid) {
            const targetImageUid = registrationState.selectedImageSeriesUid;
            if (targetImageUid && targetImageUid !== selectedImageUid) {
                setSelectedImageUid(targetImageUid);
            }
            return;
        }

        // Sync from secondary image when no registration is active
        // (e.g. same-FOR fusion set from another step)
        const secondarySeriesUid = selectedSecondaryImage?.seriesUid;
        if (secondarySeriesUid && secondarySeriesUid !== selectedImageUid) {
            setSelectedImageUid(secondarySeriesUid);
        } else if (!secondarySeriesUid && selectedImageUid) {
            setSelectedImageUid(undefined);
        }
    }, [registrationState, selectedSecondaryImage, selectedImageUid]);

    const hasForwardRegistrationsForImage = useCallback(
        (image: AvailableImage): boolean => {
            const imageReg = registrationState.imageRegistrations.find(
                (r) => r.imageSeriesUid === image.seriesInstanceUID
            );
            if (!imageReg) {
                return false;
            }
            // Only count primary→secondary registrations
            const secondaryForUid = image.imageObject?.frameOfReferenceUid;
            if (!secondaryForUid) {
                return imageReg.registrations.length > 0;
            }
            return imageReg.registrations.some(
                (reg) => reg.targetFrameOfReferenceUid === secondaryForUid
            );
        },
        [registrationState.imageRegistrations]
    );

    const handleSelectImage = useCallback(
        (image: AvailableImage) => {
            const isDeselecting = selectedImageUid === image.seriesInstanceUID;

            selectRegistration(undefined);
            handleChangeSecondaryImage(null);

            if (isDeselecting) {
                setSelectedImageUid(undefined);
                return;
            }

            setSelectedImageUid(image.seriesInstanceUID);

            // If image is loaded, has different FOR, and no
            // registrations exist, prompt to create one.
            const sharesFOR =
                image.imageObject?.frameOfReferenceUid ===
                primaryImage.frameOfReferenceUid;
            if (
                image.imageObject &&
                !sharesFOR &&
                !hasForwardRegistrationsForImage(image)
            ) {
                setCreateRegImage(image);
            }
        },
        [
            selectedImageUid,
            primaryImage,
            selectRegistration,
            handleChangeSecondaryImage,
            hasForwardRegistrationsForImage,
        ]
    );

    const handleConfirmCreateRegistration = useCallback(() => {
        if (createRegImage?.imageObject) {
            domainEventBus.publish(
                new RegistrationCreateRequestedEvent(
                    primaryImage.seriesUid,
                    createRegImage.imageObject.seriesUid
                )
            );
        }
        setCreateRegImage(undefined);
    }, [createRegImage, primaryImage.seriesUid]);

    const handleCancelCreateRegistration = useCallback(() => {
        setCreateRegImage(undefined);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setSelectedImageUid(undefined);
        selectRegistration(undefined);
        handleChangeSecondaryImage(null);
    }, [selectRegistration, handleChangeSecondaryImage]);

    const toggleDetailsPanelExpanded = useCallback(() => {
        setIsDetailsPanelExpanded((prev) => !prev);
    }, []);

    const selectedImage = useMemo(
        () =>
            availableImages.find(
                (img) => img.seriesInstanceUID === selectedImageUid
            ),
        [availableImages, selectedImageUid]
    );

    const isPrimarySelected = selectedImageUid === primaryImage.seriesUid;

    return (
        <Box
            position="relative"
            display="grid"
            gridTemplateRows="auto 1fr auto"
            gap={1}
            sx={{ overflowX: 'hidden', height: '100%' }}
        >
            <Box display="grid" gap={1}>
                <StepHeader titleId="workflow.imageRegistration" />
                <Divider />
                <PrimaryImageHeader
                    primaryImage={primaryImage}
                    availableImages={availableImages}
                    onChangePrimaryImage={onChangePrimaryImage}
                />
            </Box>
            <Box position="relative" height="100%">
                <Box
                    position="absolute"
                    width="100%"
                    height="100%"
                    sx={{ overflowY: 'auto', overflowX: 'hidden' }}
                >
                    <ImageList
                        primaryImage={primaryImage}
                        availableImages={availableImages}
                        selectedImageUid={selectedImageUid}
                        onSelectImage={handleSelectImage}
                    />
                </Box>
            </Box>

            {selectedImage && selectedImageUid && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                >
                    <Divider />
                    <Box p={1.5}>
                        <Stack
                            direction="row"
                            display="grid"
                            gridTemplateColumns="1fr auto"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <RegistrationPanel
                                image={selectedImage}
                                primaryImage={primaryImage}
                                isPrimary={isPrimarySelected}
                                onClose={handleCloseDetails}
                                isExpanded={isDetailsPanelExpanded}
                                onToggleExpanded={toggleDetailsPanelExpanded}
                            />
                        </Stack>
                    </Box>
                </Box>
            )}

            <ConfirmationDialog
                open={!!createRegImage}
                title={intl.formatMessage({
                    id: 'workflow.imageRegistration.notRegisteredTitle',
                })}
                message={intl.formatMessage({
                    id: 'workflow.imageRegistration.notRegistered',
                })}
                confirmText={intl.formatMessage({
                    id: 'workflow.imageRegistration.newRegistration',
                })}
                onConfirm={handleConfirmCreateRegistration}
                onCancel={handleCancelCreateRegistration}
                mode="primary"
            />
        </Box>
    );
}
